import { getNetworkStatus } from '@/lib/network-status';
import {
  getCallDiagnostics,
  getMediaTimeoutDiagnostics,
  getNetworkDiagnosticsOnError,
} from '@/services/logger/call-diagnostics';
import { createLogger } from '@/services/logger/logger';
import { tracingService } from '@/services/tracing/tracing-service';
import { CallEventLevel, CallEventName } from '@/services/tracing/types';
import type { CallEventPayload } from '@/services/tracing/types';
import type { HubSpotOutboundCallEvent } from '@/types/hubspot';
import { HangupReason, type Json, LegStatus, VonageError } from '@vonage/client-sdk';
import { useEffect, useRef } from 'react';
import {
  ensureSessionReady,
  markCallStarting,
  markVonageCallStart,
  recordMediaTimeout,
  refreshVonageClient,
  sessionManager,
  unmarkCallStarting,
  useVonageClient,
} from '../../vonage-client-provider';
import { isNetworkFetchError, isTerminalCallStateError, serializeError } from '../error-utils';
import {
  type VoiceCallContext,
  type VoiceContact,
  useVoiceCallState,
} from '../voice-call-container';
import { withRetry } from '../with-retry';

const log = createLogger('OutgoingCall', ['calls']);

const knownErrorsForServerCall = [
  'voice:error:media_timeout', // this normally means there is a network issue
];

const SESSION_START_RECOVERY_REASON = 'outgoing-call-start-recovery';

/** Build the `error` payload for `call:hangup`, preserving the underlying SDK / network error for tracing. */
const toHangupError = (code: string, error: unknown): { code: string; message?: string } => {
  if (error instanceof VonageError) {
    return { code, message: error.code ? `${error.code}: ${error.message}` : error.message };
  }
  if (error instanceof Error) {
    return { code, message: error.message };
  }
  if (error !== undefined) {
    return { code, message: String(error) };
  }
  return { code };
};

const isSessionStartRecoverableError = (error: unknown): boolean => {
  if (error instanceof VonageError) {
    const code = error.code?.toLowerCase() ?? '';
    if (code === 'session:error:no-active-session') {
      return true;
    }
    if (code.includes('session') || code.includes('token') || code.includes('auth')) {
      return true;
    }
  }

  if (isNetworkFetchError(error)) {
    return true;
  }

  if (!(error instanceof Error)) {
    return false;
  }

  const message = error.message.toLowerCase();
  return (
    message.includes('failed to fetch') ||
    message.includes('no active session') ||
    message.includes('expired-token') ||
    message.includes('expired token') ||
    message.includes('unauthorized') ||
    message.includes('authorization') ||
    message.includes('auth')
  );
};

/** Tracks number of calls started in this session (resets on page reload) */
let callSequenceNumber = 0;

/**
 * @internal
 * This hook is used internally by VoiceCallActionsProvider.
 * Consumers should use useVoiceCallActions() instead.
 */
export const useServerCall = () => {
  const [call, callDispatch] = useVoiceCallState();
  const vonageClient = useVonageClient();
  const lastAnsweredCallIdRef = useRef<string | null>(null);

  useEffect(() => {
    const handleLegStatusUpdate = vonageClient.on(
      'legStatusUpdate',
      async (callId, legId, status) => {
        if (String(status).toUpperCase() === LegStatus.Answered) {
          lastAnsweredCallIdRef.current = callId;
          callDispatch({ type: 'call:state:updated', callState: 'in-progress', callId });

          // Log actual audio device being used after call is answered
          try {
            const peerConn = vonageClient.getPeerConnection(callId);
            const audioSender = peerConn?.getSenders().find((s) => s.track?.kind === 'audio');
            const activeAudioTrack = audioSender?.track ?? null;
            const diagnostics = await getCallDiagnostics({ activeAudioTrack });
            log.info('Active call device info', {
              callId,
              legId,
              timestamp: Date.now(),
              diagnostics,
            });
          } catch (deviceError) {
            // Don't fail the call if device logging fails
            console.warn('Failed to log active device info:', deviceError);
          }
        }
      },
    );

    return () => {
      vonageClient.off('legStatusUpdate', handleLegStatusUpdate);
    };
    // Do NOT add `call.*` here. The handler reads only the event's own callId
    // and the stable `vonageClient`/`callDispatch`; including call state made
    // the listener detach+reattach on every reducer dispatch, and the SDK's
    // `LegStatus.Answered` event could arrive during that window and be lost —
    // leaving the call pinned at `'ringing'` with live audio. Back-to-back
    // outbound calls in the Power Dialer made this race reliably reproducible.
  }, [vonageClient, callDispatch]);

  const isRinging = call.callState === 'ringing' && call.direction === 'outgoing';
  const direction = call.direction === 'outgoing' ? 'outbound' : 'inbound';

  const startCall = async (
    callCtx: VoiceCallContext,
    contact: VoiceContact,
    context?: Json,
    hubspotCallEvent?: HubSpotOutboundCallEvent,
    options?: { signal?: AbortSignal },
  ): Promise<boolean> => {
    const signal = options?.signal;
    const isAborted = () => signal?.aborted === true;

    if (isAborted()) {
      return false;
    }

    if (call.callState !== 'idle') {
      console.warn(`[OutboundCall]: startCall - Cannot start call on state ${call.callState}`);
      return false;
    }

    callSequenceNumber++;
    const currentCallSeq = callSequenceNumber;
    const connectionHealth = sessionManager.getConnectionState();

    const mergedContext = {
      ...context,
      to: callCtx.to,
      origin_number: callCtx.origin_number,
      ...(hubspotCallEvent && {
        hubspot_owner_id: hubspotCallEvent.ownerId,
        hubspot_object_id: hubspotCallEvent.objectId,
        hubspot_object_type: hubspotCallEvent.objectType,
      }),
    } satisfies VoiceCallContext;

    const diagnostics = await getCallDiagnostics();

    if (isAborted()) {
      return false;
    }

    if (!diagnostics.network.isOnline) {
      log.error('Outgoing call failed', {
        reason: 'pre_call_network_offline',
        callSequenceNumber: currentCallSeq,
        connectionHealth,
        timestamp: Date.now(),
        diagnostics,
      });
      const preCallCtx: CallEventPayload = {
        direction: 'outbound',
        ...(callCtx.origin_number && { from: callCtx.origin_number }),
        ...(callCtx.to && { to: callCtx.to }),
      };
      tracingService.trackEvent(CallEventName.AttemptCallFailed, CallEventLevel.Error, {
        ...preCallCtx,
        errorMessage: 'pre_call_network_offline',
      });
      tracingService.trackEvent(CallEventName.DeviceCallFailed, CallEventLevel.Error, {
        ...preCallCtx,
        reason: CallEventName.AttemptCallFailed,
        stage: 'call_setup',
        errorPayload: {
          reason: 'pre_call_network_offline',
          callSequenceNumber: currentCallSeq,
          connectionHealth,
        },
      });
      return false;
    }

    if (diagnostics.network.quality === 'slow') {
      log.info('Outgoing call attempt', {
        to: callCtx.to,
        from: callCtx.origin_number,
        reason: 'pre_call_network_degraded',
        callSequenceNumber: currentCallSeq,
        connectionHealth,
        timestamp: Date.now(),
        diagnostics,
      });
    }

    log.info('Outgoing call attempt', {
      to: callCtx.to,
      from: callCtx.origin_number,
      callSequenceNumber: currentCallSeq,
      connectionHealth,
      timestamp: Date.now(),
      diagnostics,
    });

    // Pre-check mic to catch device issues before SDK timeout
    try {
      if (isAborted()) {
        return false;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      for (const track of stream.getTracks()) {
        track.stop();
      }
      if (isAborted()) {
        return false;
      }
    } catch (micError) {
      const micErrorMessage = micError instanceof Error ? micError.message : String(micError);
      log.error('Outgoing call failed', {
        reason: 'mic_unavailable',
        error: micErrorMessage,
        errorName: micError instanceof DOMException ? micError.name : undefined,
        callSequenceNumber: currentCallSeq,
        connectionHealth,
        timestamp: Date.now(),
        diagnostics,
      });
      const preCallCtx: CallEventPayload = {
        direction: 'outbound',
        ...(callCtx.origin_number && { from: callCtx.origin_number }),
        ...(callCtx.to && { to: callCtx.to }),
      };
      tracingService.trackEvent(CallEventName.AttemptCallFailed, CallEventLevel.Error, {
        ...preCallCtx,
        errorMessage: micErrorMessage,
      });
      tracingService.trackEvent(CallEventName.DeviceCallFailed, CallEventLevel.Error, {
        ...preCallCtx,
        reason: CallEventName.AttemptCallFailed,
        stage: 'call_setup',
        errorPayload: {
          reason: 'mic_unavailable',
          errorMessage: micErrorMessage,
          errorName: micError instanceof DOMException ? micError.name : undefined,
          callSequenceNumber: currentCallSeq,
          connectionHealth,
        },
      });
      return false;
    }

    let callStarted = false;
    let failureHandled = false;

    const attemptCall = async () => {
      if (isAborted()) {
        return;
      }
      // Gate the heartbeat *before* we dispatch and await the session-ready
      // barrier. `ensureSessionReady()` can resolve immediately if no refresh
      // is pending; without this flag, a heartbeat tick in the microtask gap
      // between that resolve and `serverCall()` would start its own refresh
      // and race the SDK handshake. The reducer-driven active-call marker
      // doesn't help here because `callId` isn't set until `serverCall()`
      // returns (after this window has already closed).
      markCallStarting();
      try {
        callDispatch({
          type: 'call:initiating',
          callCtx: mergedContext,
          contact,
        });
        // Wait for any in-flight session refresh before invoking the SDK.
        // Without this, a heartbeat/sessionError refresh that is still inside
        // the SDK's `startVerifyResponseTimer(5000)` handshake window will
        // cause `serverCall()` to throw `SessionNoActiveSessionError`, and
        // `withRetry`'s immediate retry lands in the same window → both
        // attempts fail. Resolves immediately when no refresh is pending.
        await ensureSessionReady();
        if (isAborted()) {
          callDispatch({
            type: 'call:hangup',
            reason: HangupReason.LocalHangup,
            source: 'outgoing_aborted_pre_sdk',
          });
          return;
        }
        // `call:initiating` fired `attempt_call`, minting the trace id. Forward
        // it to the SIP backend in the server-call custom data so the call leg
        // can be joined to the events/log stream by `call_trace_id`. Read here
        // (not when `mergedContext` was built) because the id only exists after
        // the dispatch above, and a retried attempt re-mints its own id.
        const callTraceId = tracingService.getPendingTraceId();
        const serverCallContext = callTraceId
          ? { ...mergedContext, call_trace_id: callTraceId }
          : mergedContext;
        const callId = await vonageClient.serverCall(serverCallContext);

        if (isAborted()) {
          try {
            await vonageClient.hangup(callId);
          } catch {
            // Best effort: the user cancelled before the SDK state exposed
            // the call id, so direct hangup is the only available cleanup.
          }
          callDispatch({
            type: 'call:hangup',
            reason: HangupReason.LocalHangup,
            source: 'outgoing_aborted_post_sdk',
          });
          return;
        }

        callDispatch({ type: 'call:initiated', callId, callCtx: mergedContext, contact });
        if (lastAnsweredCallIdRef.current === callId) {
          lastAnsweredCallIdRef.current = null;
          callDispatch({ type: 'call:state:updated', callState: 'in-progress', callId });
        }
        // Snapshot session-create counter so any subsequent
        // `call:phantom:detected` can report how many session rotations hit
        // during this call's lifetime.
        markVonageCallStart();
        callStarted = true;
      } finally {
        unmarkCallStarting();
      }
    };

    const recoverSessionForCallStart = async (error: unknown): Promise<boolean> => {
      const failureNetwork = getNetworkStatus();

      if (!failureNetwork.isOnline) {
        log.error('Outgoing call failed', {
          reason: 'session_start_recovery_skipped_offline',
          ...serializeError(error),
          callSequenceNumber: currentCallSeq,
          connectionHealth,
          timestamp: Date.now(),
          diagnostics,
          failureNetworkStatus: failureNetwork,
        });
        failureHandled = true;
        callDispatch({
          type: 'call:hangup',
          reason: 'LOCAL_ERROR',
          error: toHangupError('session_start_recovery_skipped_offline', error),
        });
        return false;
      }

      log.warn('Outgoing call session recovery attempt', {
        reason: SESSION_START_RECOVERY_REASON,
        ...serializeError(error),
        callSequenceNumber: currentCallSeq,
        connectionHealth,
        timestamp: Date.now(),
        diagnostics,
        failureNetworkStatus: failureNetwork,
      });

      if (isNetworkFetchError(error)) {
        Promise.resolve(getNetworkDiagnosticsOnError())
          .then((enriched) => {
            log.info('Outgoing call failed', {
              reason: 'network_fetch_error_enriched',
              ...serializeError(error),
              timestamp: Date.now(),
              enrichedDiagnostics: enriched,
            });
          })
          .catch(() => {});
      }

      try {
        await refreshVonageClient({
          trigger: { type: 'manual-reset', reason: SESSION_START_RECOVERY_REASON },
        });
        await ensureSessionReady();
        log.info('Outgoing call session recovery succeeded', {
          reason: SESSION_START_RECOVERY_REASON,
          callSequenceNumber: currentCallSeq,
          timestamp: Date.now(),
        });
        return true;
      } catch (refreshError) {
        log.error('Outgoing call failed', {
          reason: 'session_start_recovery_failed',
          ...serializeError(refreshError),
          originalError: serializeError(error),
          callSequenceNumber: currentCallSeq,
          connectionHealth: sessionManager.getConnectionState(),
          timestamp: Date.now(),
          diagnostics,
          failureNetworkStatus: getNetworkStatus(),
        });
        failureHandled = true;
        callDispatch({
          type: 'call:hangup',
          reason: 'LOCAL_ERROR',
          error: toHangupError('session_start_recovery_failed', refreshError),
        });
        return false;
      }
    };

    const handleCallFailure = async (error: unknown) => {
      if (isSessionStartRecoverableError(error)) {
        const recovered = await recoverSessionForCallStart(error);
        return recovered ? undefined : false;
      }

      if (error instanceof VonageError) {
        if (knownErrorsForServerCall.includes(error.code ?? '')) {
          log.error('Outgoing call failed', {
            reason: 'media_timeout',
            ...serializeError(error),
            callSequenceNumber: currentCallSeq,
            connectionHealth,
            timestamp: Date.now(),
            diagnostics,
          });

          // Fire enriched media timeout diagnostics (fire-and-forget)
          getMediaTimeoutDiagnostics()
            .then((enriched) => {
              log.info('Outgoing call failed', {
                reason: 'media_timeout_enriched',
                timestamp: Date.now(),
                mediaTimeoutDiagnostics: enriched,
              });
            })
            .catch(() => {});

          // Single media timeouts do not rotate the Vonage session. The
          // cleanup that `refreshSession()` used to do implicitly (via
          // session-error handlers) is now explicit:
          // - dispatch `call:hangup` to reset the reducer to idle
          // - record the timeout in the module-level rolling window
          // If ≥3 timeouts hit a 2-min window, `recordMediaTimeout` triggers
          // exactly one controlled manual-reset refresh. Normal single-timeout
          // cases do not touch the session.
          // media_timeout fires from `vonageClient.serverCall()` rejecting
          // before it resolves → `call:initiated` was never dispatched →
          // `call.callId` is undefined at this point. Use `callSequenceNumber`
          // as the correlator instead of emitting a misleading `callId` field.
          log.warn('Media timeout', {
            callSequenceNumber: currentCallSeq,
            connectionHealth,
            timestamp: Date.now(),
          });
          tracingService.trackEvent(CallEventName.CallMediaTimeout, CallEventLevel.Warning, {
            callSequenceNumber: currentCallSeq,
            connectionHealth,
          });
          failureHandled = true;
          callDispatch({
            type: 'call:hangup',
            reason: 'LOCAL_ERROR',
            error: toHangupError('media_timeout', error),
          });
          recordMediaTimeout();
          return false; // abort retry
        }
      }

      if (isNetworkFetchError(error)) {
        const failureNetwork = getNetworkStatus();
        log.error('Outgoing call failed', {
          reason: 'network_fetch_error',
          ...serializeError(error),
          callSequenceNumber: currentCallSeq,
          connectionHealth,
          timestamp: Date.now(),
          diagnostics,
          failureNetworkStatus: failureNetwork,
        });
        // Fire enriched diagnostics probe (fire-and-forget)
        getNetworkDiagnosticsOnError()
          .then((enriched) => {
            log.info('Outgoing call failed', {
              reason: 'network_fetch_error_enriched',
              ...serializeError(error),
              timestamp: Date.now(),
              enrichedDiagnostics: enriched,
            });
          })
          .catch(() => {});
        failureHandled = true;
        callDispatch({
          type: 'call:hangup',
          reason: 'LOCAL_ERROR',
          error: toHangupError('network_fetch_error', error),
        });
        return false; // abort retry — session refresh will also fail on broken network
      }

      if (isTerminalCallStateError(error)) {
        log.error('Outgoing call failed', {
          reason: 'terminal_call_state_error',
          ...serializeError(error),
          callSequenceNumber: currentCallSeq,
          connectionHealth,
          timestamp: Date.now(),
          diagnostics,
        });
        failureHandled = true;
        callDispatch({
          type: 'call:hangup',
          reason: 'LOCAL_ERROR',
          error: toHangupError('terminal_call_state_error', error),
        });
        return false;
      }

      // Unknown outgoing-call errors do not trigger a session refresh.
      // `withRetry` retries once; if that also fails, `onFailure` clears UI
      // and Sentry already captures the error via `withRetry.sentry`.
      log.error('Outgoing call failed', {
        reason: 'unknown_error_retrying',
        ...serializeError(error),
        callSequenceNumber: currentCallSeq,
        connectionHealth,
        timestamp: Date.now(),
        diagnostics,
        sessionRefreshAttempted: false,
        sessionRefreshSkipReason: 'unknown_error',
      });
    };

    try {
      await withRetry(attemptCall, {
        name: 'attemptCall',
        maxRetries: 1,
        onRetry: handleCallFailure,
        skipFailureIfHandled: true,
        onFailure: (error: unknown) => {
          log.error('Outgoing call failed', {
            reason: 'max_retries_exceeded',
            ...serializeError(error),
            callSequenceNumber: currentCallSeq,
            connectionHealth,
            timestamp: Date.now(),
            diagnostics,
          });
          failureHandled = true;
          callDispatch({
            type: 'call:hangup',
            reason: 'LOCAL_ERROR',
            error: toHangupError('max_retries_exceeded', error),
          });
        },
        sentry: {
          tag: 'vonage_server_call_error',
          extra: {
            to: mergedContext.to,
            from: mergedContext.origin_number,
          },
        },
      });
      return callStarted;
    } catch (error) {
      if (failureHandled) {
        return false;
      }
      log.error('Outgoing call failed', {
        reason: 'unexpected_error',
        ...serializeError(error),
        callSequenceNumber: currentCallSeq,
        connectionHealth,
        timestamp: Date.now(),
        diagnostics,
      });
      return false;
    }
  };

  return { startCall, isRinging, direction };
};
